import {
  CostCode,
  Equipment,
  Member,
  OrganizationCustomFields,
  OrganizationCustomFieldsTypes,
  Project,
} from '__generated__/graphql';
import { t } from 'i18next';
import { isNil } from 'lodash';

export const MAX_CUSTOM_FIELDS = 5;

export type CFIndex = 1 | 2 | 3 | 4 | 5;

export type CFTitleKey = `title${CFIndex}`;
export type CFSubtitleKey = `subtitle${CFIndex}`;
export type CFAdminOnlyKey = `adminOnly${CFIndex}`;
export type CFKey = `customField${CFIndex}`;

export interface ICustomFieldTableRow {
  title: string;
  subtitle: string;
  adminOnly: boolean;
}

export interface ICustomFieldWithValue {
  id: CFKey;
  value: string | null;
  title: string | null;
  subtitle: string | null;
  adminOnly: boolean;
}

export interface ICustomFieldSimpleObject {
  fieldName: string;
  value: string;
}

export function getCustomFieldValues(
  model:
    | Pick<Equipment, 'customField1' | 'customField2' | 'customField3' | 'customField4' | 'customField5'>
    | Pick<Member, 'customField1' | 'customField2' | 'customField3' | 'customField4' | 'customField5'>
    | Pick<CostCode, 'customField1' | 'customField2' | 'customField3' | 'customField4' | 'customField5'>
    | Pick<Project, 'customField1' | 'customField2' | 'customField3' | 'customField4' | 'customField5'>,
  isAdmin: boolean,
  orgCustomFields?: OrganizationCustomFields | null
): ICustomFieldWithValue[] {
  const out: ICustomFieldWithValue[] = [];

  if (!isNil(orgCustomFields)) {
    for (let i = 1 as CFIndex; i <= MAX_CUSTOM_FIELDS; i = (i + 1) as CFIndex) {
      const id = `customField${i}` as CFKey;
      const titleKey = `title${i}` as CFTitleKey;
      const subtitleKey = `subtitle${i}` as CFSubtitleKey;
      const adminKey = `adminOnly${i}` as CFAdminOnlyKey;

      const title = orgCustomFields[titleKey] ?? null;
      const subtitle = orgCustomFields[subtitleKey] ?? null;
      const adminOnly = orgCustomFields[adminKey] ?? false;

      if (!title?.trim() || (adminOnly && !isAdmin)) {
        continue;
      }

      const rawValue = model[id];
      const value = typeof rawValue === 'string' ? rawValue : null;

      out.push({ id, title, subtitle, adminOnly, value });
    }
  }

  return out;
}

export function getCustomFieldTitles(isAdmin: boolean, orgCustomFields?: OrganizationCustomFields | null): string[] {
  const out: string[] = [];

  if (!isNil(orgCustomFields)) {
    for (let i = 1 as CFIndex; i <= MAX_CUSTOM_FIELDS; i = (i + 1) as CFIndex) {
      const titleKey = `title${i}` as CFTitleKey;
      const adminKey = `adminOnly${i}` as CFAdminOnlyKey;

      const title = orgCustomFields[titleKey] ?? null;
      const adminOnly = orgCustomFields[adminKey] ?? false;

      if (!title?.trim() || (adminOnly && !isAdmin)) {
        continue;
      }

      out.push(title);
    }
  }

  return out;
}

export function extractCustomFields(fields: OrganizationCustomFields) {
  const result: ICustomFieldTableRow[] = [];

  for (let i = 1; i <= 5; i++) {
    const title = fields[`title${i}` as keyof OrganizationCustomFields];
    const subtitle = fields[`subtitle${i}` as keyof OrganizationCustomFields];
    const adminOnly = fields[`adminOnly${i}` as keyof OrganizationCustomFields];

    if (typeof title === 'string' && title.trim()) {
      result.push({
        title: typeof title === 'string' ? title : '',
        subtitle: typeof subtitle === 'string' ? subtitle : '',
        adminOnly: typeof adminOnly === 'boolean' ? adminOnly : false,
      });
    }
  }

  return result as ICustomFieldTableRow[];
}

export function parseTypeString(type: OrganizationCustomFieldsTypes) {
  switch (type) {
    case OrganizationCustomFieldsTypes.Member:
      return t('Employee');
    case OrganizationCustomFieldsTypes.Project:
      return t('Project');
    case OrganizationCustomFieldsTypes.CostCode:
      return t('Cost Code');
    case OrganizationCustomFieldsTypes.Equipment:
      return t('Equipment');
    default:
      return '';
  }
}

export function parseTypeStringToEndpoint(type: OrganizationCustomFieldsTypes) {
  switch (type) {
    case OrganizationCustomFieldsTypes.Member:
      return 'member';
    case OrganizationCustomFieldsTypes.Project:
      return 'project';
    case OrganizationCustomFieldsTypes.CostCode:
      return 'cost-code';
    case OrganizationCustomFieldsTypes.Equipment:
      return 'equipment';
    default:
      return '';
  }
}

export function mapCustomFieldsToColumns(fields: ICustomFieldTableRow[]) {
  return fields.map((field, index) => {
    return {
      key: `customField${index + 1}`,
      title: field.title,
      sort: true,
      visible: false, // Custom fields are not visible by default
      visibleByDefault: false,
    };
  });
}
